/** Small shared UI for feed chat rows (thumbs, copy). */
import { useState, useCallback } from "react";
import toast from "react-hot-toast";
import { ThumbsUp, ThumbsDown, Copy, Check } from "lucide-react";

export function FeedbackButtons({
  itemId,
  onFeedback,
}: {
  itemId: string;
  onFeedback: (id: string, q: "good" | "bad") => void;
}) {
  const [feedback, setFeedback] = useState<"good" | "bad" | null>(null);
  if (feedback) {
    return (
      <span className="text-[10px] text-muted-foreground/75 ml-1">
        {feedback === "good" ? "Thanks!" : "Got it, I'll improve."}
      </span>
    );
  }
  return (
    <>
      <button
        onClick={() => {
          setFeedback("good");
          onFeedback(itemId, "good");
        }}
        className="p-1 rounded hover:bg-muted/50 text-muted-foreground/75 hover:text-green-400 transition-colors"
        title="Good response"
      >
        <ThumbsUp className="w-3 h-3" />
      </button>
      <button
        onClick={() => {
          setFeedback("bad");
          onFeedback(itemId, "bad");
        }}
        className="p-1 rounded hover:bg-muted/50 text-muted-foreground/75 hover:text-red-400 transition-colors"
        title="Bad response"
      >
        <ThumbsDown className="w-3 h-3" />
      </button>
    </>
  );
}

export function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Copy failed — check browser permissions");
    }
  }, [text]);
  return (
    <button
      onClick={handleCopy}
      className="inline-flex items-center gap-1 p-1 px-2 rounded hover:bg-muted/50 text-muted-foreground/75 hover:text-foreground transition-colors text-[11px] whitespace-nowrap"
      title="Copy to clipboard"
    >
      {copied ? (
        <Check className="w-3 h-3 flex-shrink-0" />
      ) : (
        <Copy className="w-3 h-3 flex-shrink-0" />
      )}
      <span>{copied ? "Copied" : "Copy"}</span>
    </button>
  );
}
